import React, { FC } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Link as RouterLink } from 'react-router-dom'

import { ApiTableColumn, FetchProps } from '@e3dc-react/shell/Elements/ApiView/ApiView'
import { Link } from '@mui/material'
import { UseFetchProps } from '@e3dc-react/shell/Hooks/useFetch'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Installation } from 'src/Libs/installations/models/installation.model'

const useStyles = makeStyles()(theme => ({
  link: {
    color: theme.palette.primary.main,
  },
}))

const emptySymbol = '-'

interface LinkComponentProps {
  link: string
  text: string | number
}

const LinkComponent: FC<LinkComponentProps> = ({ link, text }) => {
  const { classes } = useStyles()

  return (
    <Link className={classes.link} component={RouterLink} to={link}>
      {text}
    </Link>
  )
}

const MasterDeviceColumn: FC<Installation> = ({ emsMasterDeviceId, id }) => {
  if (!emsMasterDeviceId || !id) {
    return emptySymbol
  }

  return <LinkComponent link={`/overview/installations/${id}/${emsMasterDeviceId}/details`} text={emsMasterDeviceId} />
}

const OwnerColumn: FC<Installation> = ({ ownerAccountId, ownerAccountType, ownerCompanyName, ownerFirstName, ownerLastName }) => {
  const isCompany = ownerAccountType === 'company'
  const link = isCompany ? `/overview/companies/${ownerAccountId}` : `/overview/users/${ownerAccountId}`
  const ownerFullName = ownerFirstName || ownerLastName ? [ownerFirstName ?? '', ownerLastName ?? ''].join(' ') : emptySymbol
  const linkContent = isCompany ? ownerCompanyName ?? emptySymbol : ownerFullName

  if (!ownerAccountId) {
    return emptySymbol
  }

  return <LinkComponent link={link} text={linkContent} />
}

const InstallerColumn: FC<Installation> = ({ createdBy, createdByFirstName, createdByLastName }) => {
  const fullName = [createdByFirstName ?? '', createdByLastName ?? ''].join(' ')

  if (!createdBy || !fullName.length) {
    return emptySymbol
  }

  return <LinkComponent link={`/overview/users/${createdBy}`} text={fullName} />
}

/**
 * This hook holds all data for the company installations api view
 */
export const useCompanyInstallationsTableData = (
  companyId: number
): {
  fetchProps: UseFetchProps
  columns: ApiTableColumn<Installation>[]
} => {
  const { t } = useCustomTranslation()

  const fetchProps: FetchProps = {
    url: `installations/by-company-id/${companyId}`,
    orderBy: '-id',
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
  }

  const columns: ApiTableColumn<Installation>[] = [
    {
      name: 'id',
      label: t('administration.companies.installations.table.id'),
      value: ({ id }) => <LinkComponent link={`/overview/installations/${id}`} text={id} />,
    },
    {
      name: 'emsMasterDeviceId',
      label: t('administration.companies.installations.table.master-device-id'),
      value: MasterDeviceColumn,
    },
    {
      name: 'projectName',
      label: t('administration.companies.installations.table.installation-name'),
      value: ({ projectName }) => projectName ?? emptySymbol,
    },
    {
      name: 'ownerId',
      label: t('administration.companies.installations.table.owner'),
      value: OwnerColumn,
    },
    {
      name: 'countryCode',
      label: t('administration.companies.installations.table.country'),
      value: ({ countryCode }) => (countryCode ? t(`common.countries.short.${countryCode}`) : emptySymbol),
    },
    {
      name: 'zip',
      label: t('administration.companies.installations.table.zip'),
      value: ({ zip }) => zip || emptySymbol,
    },
    {
      name: 'city',
      label: t('administration.companies.installations.table.city'),
      value: ({ city }) => city || emptySymbol,
    },
    {
      name: 'streetName',
      label: t('administration.companies.installations.table.address'),
      value: ({ streetName, streetNumber }) => (streetName && streetNumber ? `${streetName}, ${streetNumber}` : emptySymbol),
    },
    {
      name: 'createdBy',
      label: t('administration.companies.installations.table.installer'),
      value: InstallerColumn,
    },
    {
      name: 'createdByIpin',
      label: t('administration.companies.installations.table.installer-ipin'),
      value: ({ createdByIpin }) => createdByIpin ?? emptySymbol,
    },
  ]

  return { columns, fetchProps }
}
